import { PALETTE, entityLabel } from './activityReport'

/**
 * Every report the dashboard renders. Mirrors `activity/registry.py`; the ids
 * and action names must match it exactly, because the server sends rows keyed by
 * those action names.
 *
 * Adding a report is one entry here plus one in the Python registry. Nothing
 * else -- no new query, hook, component or chart code.
 *
 * `metric` mirrors the two count columns the server returns: creations count
 * distinct objects, updates count history events (several fire per object).
 *
 * Colours come from the validated 8-slot palette and are assigned per report, so
 * within one report a colour always means the same action. They are not shared
 * across reports; two reports are never on screen at once.
 */

const created = (value, label, entity, color) => ({
    value,
    label,
    entity,
    color,
    mode: 'created',
    metric: 'objectsCount',
})

const updated = (value, label, entity, color) => ({
    value,
    label,
    entity,
    color,
    mode: 'updated',
    metric: 'eventsCount',
})

// The chart layout every activity report uses. Declared once and shared, so the
// reports stay visually consistent and a change lands everywhere at once. A
// report that needs something different overrides `charts` in its own entry.
const STANDARD_CHARTS = [
    { type: 'trend', title: 'Daily activity', caption: 'Events per day across every action in this report' },
    { type: 'actionMix', groupBy: 'userName', title: 'By staff member', caption: 'Created vs edited, this range' },
    { type: 'actionMix', groupBy: 'branchName', title: 'By branch', caption: 'Created vs edited, this range' },
    { type: 'heatmap', title: 'Who worked on this, by day', caption: 'Darker cells mean more events that day' },
]

// The Users view every report shares: who did the work, not what it was on.
// Declared once, exactly like STANDARD_CHARTS, because the whole point of this
// view is that it is the same generic three-chart layout on every report --
// nothing here ever needs a report-specific override. Built from the report's
// own actions (via `actionMeta` / `chart.actions` fallback in ReportBody), so
// it is naturally scoped to that report's own activity, never another one's.
const USER_CHARTS = [
    { type: 'leaderboard', title: 'Effort by user', caption: 'Two bars per person -- created above, updated below' },
    {
        type: 'trend',
        title: 'Activity over time',
        caption: 'Daily totals by record type, this range -- coloured to match the leaderboard above',
        // Coloured by entity, not action, so this shares its colour axis with
        // the leaderboard above it rather than inventing a second one. An
        // unscoped action-keyed trend needs one colour per action -- for
        // `incidents` that is 10 series over an 8-slot palette, which both
        // collides two unrelated actions onto the same colour and disagrees
        // with the leaderboard about what `PALETTE[0]` means on this tab. See
        // `seriesForChart` / `entityPaletteFor` in this file.
        colorBy: 'entity',
    },
    { type: 'heatmap', title: 'Daily intensity', caption: 'Darker cells mean more events that day' },
]

export const REPORTS = {
    incidents: {
        id: 'incidents',
        name: 'Incidents',
        subtitle: 'Incidents, parties and documents opened and edited, counted from the history log — not live totals.',
        // The "Incidents created"/"Incidents edited" stat tiles must count the
        // incident itself, not its parties or documents -- otherwise
        // "Incidents created" silently includes a document upload, which is
        // not an incident. See `headlineActions` below, which scopes those
        // two tiles down to just the incident actions; every other action
        // still counts toward "Total edit events" (see `buildKpis`).
        noun: 'Incidents',
        headlineActions: ['INCIDENT_CREATED', 'INCIDENT_UPDATED'],
        actions: [
            created('INCIDENT_CREATED', 'Incident opened', 'INCIDENT', PALETTE[0]),
            updated('INCIDENT_UPDATED', 'Incident edited', 'INCIDENT', PALETTE[1]),
            created('PARTY_CREATED', 'Party added', 'PARTY', PALETTE[2]),
            updated('PARTY_UPDATED', 'Party edited', 'PARTY', PALETTE[3]),
            created('PARTY_PEOPLE_CREATED', 'Party person added', 'PARTY_PEOPLE', PALETTE[4]),
            updated('PARTY_PEOPLE_UPDATED', 'Party person edited', 'PARTY_PEOPLE', PALETTE[5]),
            created('PARTY_VEHICLE_CREATED', 'Party vehicle added', 'PARTY_VEHICLE', PALETTE[6]),
            updated('PARTY_VEHICLE_UPDATED', 'Party vehicle edited', 'PARTY_VEHICLE', PALETTE[7]),
            created('DOCUMENT_UPLOADED', 'Document uploaded', 'DOCUMENT', PALETTE[0]),
            updated('DOCUMENT_UPDATED', 'Document edited', 'DOCUMENT', PALETTE[1]),
        ],
        // 10 actions is more than the 8-slot palette can give distinct
        // colours, but that only matters where colour is the sole
        // differentiator -- a chart legend, or two touching bar segments.
        // This report's table (`DataTable`) is not that: every column
        // already carries its own text header ("Incident opened" vs
        // "Document uploaded"), and the value cell's colour chip is a
        // decorative highlight, not what tells columns apart. `PALETTE`'s
        // own comment says as much -- the relief rule is that every chart
        // using low-contrast slots ships a table view, i.e. the table is
        // precisely the place viewers are not meant to rely on colour. So the
        // table renders one column per action here, same as `insurance` (6
        // actions, also uncollapsed) already does.
        //
        // The daily trend below is the one chart that does combine every
        // entity: the user asked for all five record types, both created
        // and updated, on one line chart -- ten series over an eight-slot
        // palette. It solves that by colouring per entity (`colorBy:
        // 'entityPerAction'`, five hues, matching the leaderboard) and
        // carrying created-vs-updated as line style instead of a second
        // colour -- solid for created, dashed for updated. See
        // `seriesForChart` for how that is derived, and `TrendLine` for how
        // the dashing renders. An incident is a case and a document is a
        // file attached to one, so this still mixes units by count; the
        // chart's caption says so rather than implying they are
        // comparable.
        //
        // Every other chart below declares its own `actions`, scoped to one
        // entity (incidents, parties, or documents), so no single one of
        // them ever mixes more than two colours under a per-action legend,
        // and per-action detail (e.g. "Incident opened" vs "Document
        // uploaded") survives instead of being flattened to generic
        // created/edited labels. The party actions (party, party person,
        // party vehicle x created/updated) get their own chart here too --
        // those six actions map to `PALETTE[2]`-`PALETTE[7]`, all distinct,
        // so a per-action legend is safe there as well, consistent with the
        // incidents and documents charts either side of it.
        charts: [
            {
                type: 'trend',
                title: 'Daily incident activity',
                caption: 'Every record type this report tracks, per day -- solid lines are new records, dashed are edits, colour matches the leaderboard. Mixes cases, parties and documents, so read this as overall activity volume, not an incident count.',
                colorBy: 'entityPerAction',
                actions: [
                    'INCIDENT_CREATED',
                    'INCIDENT_UPDATED',
                    'PARTY_CREATED',
                    'PARTY_UPDATED',
                    'PARTY_PEOPLE_CREATED',
                    'PARTY_PEOPLE_UPDATED',
                    'PARTY_VEHICLE_CREATED',
                    'PARTY_VEHICLE_UPDATED',
                    'DOCUMENT_UPLOADED',
                    'DOCUMENT_UPDATED',
                ],
            },
            {
                type: 'actionMix',
                groupBy: 'userName',
                title: 'Incidents by staff member',
                caption: 'Opened vs edited, this range',
                actions: ['INCIDENT_CREATED', 'INCIDENT_UPDATED'],
            },
            {
                type: 'actionMix',
                groupBy: 'branchName',
                title: 'Incidents by branch',
                caption: 'Opened vs edited, this range',
                actions: ['INCIDENT_CREATED', 'INCIDENT_UPDATED'],
            },
            {
                type: 'actionMix',
                groupBy: 'userName',
                title: 'Documents by staff member',
                caption: 'Uploaded vs edited, this range -- kept separate from incidents: a document is a file attached to a case, not a comparable unit',
                actions: ['DOCUMENT_UPLOADED', 'DOCUMENT_UPDATED'],
            },
            {
                type: 'actionMix',
                groupBy: 'userName',
                title: 'Parties by staff member',
                caption: 'Parties, party people and party vehicles -- added vs edited, this range',
                actions: [
                    'PARTY_CREATED',
                    'PARTY_UPDATED',
                    'PARTY_PEOPLE_CREATED',
                    'PARTY_PEOPLE_UPDATED',
                    'PARTY_VEHICLE_CREATED',
                    'PARTY_VEHICLE_UPDATED',
                ],
            },
            { type: 'heatmap', title: 'Who worked on this, by day', caption: 'Darker cells mean more events that day' },
        ],
        userCharts: USER_CHARTS,
        // Documents no longer fold into "Incidents created" (see
        // `headlineActions` above), so they get their own tile instead of
        // disappearing from the headline row entirely. Counts distinct
        // documents, not upload events: DOCUMENT_UPLOADED is a created-mode
        // action, so it already reads `objectsCount` -- the same distinct-object
        // metric "Incidents created" reads -- rather than `eventsCount`, which
        // is what "uploaded" should mean here (how many documents landed in
        // this range, not how many upload-related events fired). See
        // `buildExtraTileValue` in activityReport.js.
        extraTiles: [
            {
                key: 'documents',
                label: 'Documents uploaded',
                caption: 'distinct documents added in this range',
                actions: ['DOCUMENT_UPLOADED'],
                testId: 'kpi-documents-uploaded',
            },
        ],
    },
    claims: {
        id: 'claims',
        name: 'Claims',
        subtitle: 'Liability and subrogation claims raised and worked, counted from the history log — not live totals.',
        // Both liability and subrogation claims are claims, so all four
        // actions are headline -- no `headlineActions` override needed, the
        // default (every action in the report) already reads right here.
        noun: 'Claims',
        actions: [
            created('LIABILITY_CLAIM_CREATED', 'Liability claim opened', 'LIABILITY_CLAIM', PALETTE[0]),
            updated('LIABILITY_CLAIM_UPDATED', 'Liability claim edited', 'LIABILITY_CLAIM', PALETTE[1]),
            created('SUBROGATION_CLAIM_CREATED', 'Subro claim opened', 'SUBROGATION_CLAIM', PALETTE[2]),
            updated('SUBROGATION_CLAIM_UPDATED', 'Subro claim edited', 'SUBROGATION_CLAIM', PALETTE[3]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    reservations: {
        id: 'reservations',
        name: 'Reservations',
        subtitle: 'Reservations created, amended and cancelled, counted from the history log — not live totals.',
        noun: 'Reservations',
        actions: [
            created('RESERVATION_CREATED', 'Reservation created', 'RESERVATION', PALETTE[0]),
            updated('RESERVATION_UPDATED', 'Reservation edited', 'RESERVATION', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    agreements: {
        id: 'agreements',
        name: 'Agreements',
        subtitle: 'Rental agreements opened, edited and closed, counted from the history log — not live totals.',
        noun: 'Agreements',
        actions: [
            created('AGREEMENT_CREATED', 'Agreement opened', 'AGREEMENT', PALETTE[0]),
            updated('AGREEMENT_UPDATED', 'Agreement edited', 'AGREEMENT', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    car_returns: {
        id: 'car_returns',
        name: 'Car Returns',
        subtitle: 'Return requests raised, confirmed and closed out, counted from the history log — not live totals.',
        noun: 'Car returns',
        actions: [
            created('CAR_RETURN_CREATED', 'Return created', 'CAR_RETURN', PALETTE[0]),
            updated('CAR_RETURN_UPDATED', 'Return edited', 'CAR_RETURN', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    payment_plans: {
        id: 'payment_plans',
        name: 'Payment Plans',
        subtitle: 'Paydown plans set up, adjusted and deactivated, counted from the history log — not live totals.',
        noun: 'Payment plans',
        actions: [
            created('PAYMENT_PLAN_CREATED', 'Plan created', 'PAYMENT_PLAN', PALETTE[0]),
            updated('PAYMENT_PLAN_UPDATED', 'Plan edited', 'PAYMENT_PLAN', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    scheduled_payments: {
        id: 'scheduled_payments',
        name: 'Scheduled Payments',
        subtitle: 'Auto-charge schedules created, cancelled and retried, counted from the history log — not live totals.',
        noun: 'Scheduled payments',
        actions: [
            created('PAYMENT_SCHEDULE_CREATED', 'Schedule created', 'PAYMENT_SCHEDULE', PALETTE[0]),
            updated('PAYMENT_SCHEDULE_UPDATED', 'Schedule edited', 'PAYMENT_SCHEDULE', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    subscriptions: {
        id: 'subscriptions',
        name: 'Subscriptions',
        subtitle: 'Subscriptions started, changed and cancelled, counted from the history log — not live totals.',
        noun: 'Subscriptions',
        actions: [
            created('SUBSCRIPTION_CREATED', 'Subscription created', 'SUBSCRIPTION', PALETTE[0]),
            updated('SUBSCRIPTION_UPDATED', 'Subscription edited', 'SUBSCRIPTION', PALETTE[1]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
    insurance: {
        id: 'insurance',
        name: 'Insurance',
        subtitle: 'Driver insurance statuses and removals, counted from the history log — not live totals.',
        // "Insurance" names the primary record this report is about --
        // DRIVER_INSURANCE_STATUS -- the same relationship `incidents` has to
        // its own INCIDENT entity. DRIVER_REMOVED_REASON is a related but
        // distinct record (why a driver's insurance was removed, not the
        // insurance status itself), so it is excluded from the headline
        // tiles below for the same reason incidents excludes parties and
        // documents: "Insurance created" must not silently include a removal
        // reason. The actions are already named "Insurance created"/"Insurance
        // edited" vs "Insurance Removal recorded"/"Insurance Removal edited",
        // which is exactly this split. Removal activity still counts toward
        // "Total edit events", same as every other action in the report.
        noun: 'Insurance',
        headlineActions: ['DRIVER_INSURANCE_STATUS_CREATED', 'DRIVER_INSURANCE_STATUS_UPDATED'],
        actions: [
            created('DRIVER_INSURANCE_STATUS_CREATED', 'Insurance created', 'DRIVER_INSURANCE_STATUS', PALETTE[0]),
            updated('DRIVER_INSURANCE_STATUS_UPDATED', 'Insurance edited', 'DRIVER_INSURANCE_STATUS', PALETTE[1]),
            created('DRIVER_REMOVED_REASON_CREATED', 'Insurance Removal recorded', 'DRIVER_REMOVED_REASON', PALETTE[2]),
            updated('DRIVER_REMOVED_REASON_UPDATED', 'Insurance Removal edited', 'DRIVER_REMOVED_REASON', PALETTE[3]),
        ],
        charts: STANDARD_CHARTS,
        userCharts: USER_CHARTS,
    },
}

/**
 * The chart layout for a viewer restricted to their own rows: a trend chart
 * and a per-branch chart, in place of the report's full layout. Built from
 * the report's own `charts` -- reusing its actual trend chart and its actual
 * branchName-grouped `actionMix` chart -- rather than a hand-written parallel
 * list, so a report that scopes those charts down to a subset of its actions
 * (see `chart.actions`, and `incidents` in particular) keeps that scoping
 * here too. A hand-written list has no `actions` of its own, so
 * `seriesForChart` would fall back to the whole report's action list --
 * exactly the palette-collision and unit-mixing bug this replaces.
 *
 * The two people-comparing chart types are dropped outright, same as before:
 * `actionMix` grouped by `userName` ("by staff member") and the heatmap. A
 * one-bar chart and a one-row heatmap say nothing about one person's own
 * activity.
 */
export const selfScopedChartsFor = (report) => {
    const trendChart = report.charts.find((chart) => chart.type === 'trend')
    const branchChart = report.charts.find(
        (chart) => chart.type === 'actionMix' && chart.groupBy === 'branchName'
    )
    const charts = []
    if (trendChart) {
        charts.push({ ...trendChart, title: 'Your daily activity', caption: 'Your events per day in this range' })
    }
    if (branchChart) {
        charts.push({ ...branchChart, title: 'Your work by branch', caption: 'Created vs edited, this range' })
    }
    return charts
}

/**
 * The Users view for a viewer restricted to their own rows. `Leaderboard` and
 * the heatmap are both people-comparing charts -- a one-row leaderboard or
 * heatmap says nothing about one person's own activity, same reasoning as
 * `selfScopedChartsFor` above -- so only the trend chart survives, retitled
 * the same way.
 */
export const selfScopedUserChartsFor = (report) => {
    const trendChart = report.userCharts.find((chart) => chart.type === 'trend')
    if (!trendChart) return []
    return [{ ...trendChart, title: 'Your daily activity', caption: 'Your events per day in this range' }]
}

export const seriesFor = (report) =>
    report.actions.map((action) => ({
        key: action.value,
        label: action.label,
        color: action.color,
    }))

/**
 * Series for one chart. A chart marked `colorBy: 'entity'` (the Users-view
 * trend -- see `USER_CHARTS`) is keyed by entity, not action, so it shares
 * its colour axis with the leaderboard on the same tab: one slot per
 * distinct entity, from `entityPaletteFor`, the exact derivation
 * `buildLeaderboard` uses via `entityColorFor`. Created and updated collapse
 * into the same line there -- that chart already scopes to one series per
 * entity, not per action.
 *
 * A chart marked `colorBy: 'entityPerAction'` (the incidents daily trend --
 * see its entry above) is different: it still needs *one line per action*
 * (created and updated must stay two separate series, or an entity's two
 * counts get summed into one line), but colouring all ten of `incidents`'
 * actions flat would need ten palette slots for a chart with only eight. So
 * colour is taken from the entity palette (one hue per entity, matching the
 * leaderboard) and every series also carries `dashed: true` for its
 * `updated`-mode actions -- `TrendLine` renders that as a dashed line, so
 * created vs updated is told apart by line style instead of a second colour.
 * Five entities x two styles identifies all ten actions within an eight-slot
 * palette.
 *
 * A chart that declares `actions` without a `colorBy` is scoped to just
 * those -- one series per action, in the order given, each keeping its own
 * flat `action.color` -- which lets a chart scope itself down to a handful
 * of actions (e.g. `incidents`' per-entity charts) without mixing record
 * types under one legend. A chart with neither covers the whole report and
 * falls back to `seriesFor`, which is how every report's unscoped charts
 * keep today's behaviour.
 */
export const seriesForChart = (report, chart) => {
    if (chart.colorBy === 'entity') {
        return entityPaletteFor(report).map(({ entity, color }) => ({
            key: entity,
            label: entityLabel(entity),
            color,
        }))
    }
    if (chart.colorBy === 'entityPerAction') {
        const entityColor = entityColorFor(report)
        const actionValues = chart.actions || report.actions.map((action) => action.value)
        return actionValues.map((actionValue) => {
            const action = report.actions.find((candidate) => candidate.value === actionValue)
            return {
                key: action.value,
                label: action.label,
                color: entityColor(action.entity),
                dashed: action.mode === 'updated',
            }
        })
    }
    return chart.actions
        ? chart.actions.map((actionValue) => {
            const action = report.actions.find((candidate) => candidate.value === actionValue)
            return { key: action.value, label: action.label, color: action.color }
        })
        : seriesFor(report)
}

/**
 * Entity -> palette colour for a report's leaderboard: one slot per distinct
 * entity among the report's own actions, assigned in the order those
 * entities first appear in `report.actions`.
 *
 * This is a different colour axis from `action.color` above. `action.color`
 * distinguishes created vs updated *within* one entity (what
 * `seriesFor`/`seriesForChart` hand out, e.g. `INCIDENT_CREATED` and
 * `INCIDENT_UPDATED` get different slots), which is right for a chart legend
 * keyed by action. The leaderboard is keyed by entity instead -- it stacks
 * `created_<ENTITY>` and `updated_<ENTITY>` together under one bar per user
 * -- so it needs one colour per *entity*, not one per action. Colouring it
 * per action (what this replaces) can give a report more colour-bearing
 * entities than the palette has actions-per-entity slots for: `incidents`
 * has 10 actions across only 5 entities, so two entities ended up sharing a
 * slot even though the palette has room for all five.
 *
 * Derived from `report.actions` rather than a separate hand-maintained
 * entity table, so a report's entity list can never drift out of sync with
 * the actions that define it -- add or remove an action and the leaderboard
 * palette follows automatically.
 */
export const entityPaletteFor = (report) => {
    const entities = []
    report.actions.forEach((action) => {
        if (!entities.includes(action.entity)) entities.push(action.entity)
    })
    return entities.map((entity, index) => ({ entity, color: PALETTE[index % PALETTE.length] }))
}

/** Lookup version of `entityPaletteFor`, for `buildLeaderboard`. */
export const entityColorFor = (report) => {
    const byEntity = entityPaletteFor(report).reduce(
        (acc, { entity, color }) => ({ ...acc, [entity]: color }),
        {}
    )
    return (entityValue) => byEntity[entityValue]
}

export const getReport = (reportId) => REPORTS[reportId]

/**
 * The action values that scope a report's "created"/"edited" stat tiles --
 * `report.headlineActions` when the registry declares one (see `incidents`
 * and `insurance` above), or every one of the report's own actions
 * otherwise. That default is what lets the eight single-record-type reports
 * skip declaring `headlineActions` at all: their whole action list already
 * *is* the headline.
 */
export const headlineActionsFor = (report) => report.headlineActions || report.actions.map((action) => action.value)

/**
 * Tiles a report declares beyond the five generic ones every report shows,
 * rendered in the stat row right after them -- e.g. incidents' "Documents
 * uploaded" above. Each entry is `{ key, label, caption, actions, testId }`;
 * `actions` scopes the tile to a subset of the report's own actions, the same
 * shape `chart.actions` already uses elsewhere in this file. Registry-driven
 * so a report opts in by declaring `extraTiles` -- there is no `report.id`
 * branch in `ReportBody.js` deciding this per report -- and the default here
 * (no tiles) is what leaves the other eight reports' stat row untouched.
 */
export const extraTilesFor = (report) => report.extraTiles || []

/** The lookup every function in `activityReport.js` expects. */
export const actionMetaFor = (report) => {
    const byValue = (report?.actions || []).reduce(
        (acc, action) => ({ ...acc, [action.value]: action }),
        {}
    )
    return (actionValue) => byValue[actionValue]
}
